import { range, normalize } from '@/utils';

import {
  SPRING_WIDTH,
  SPRING_HEIGHT,
  SPRING_STEM_HEIGHT,
} from './SpringPhysics.constants';

const SIN_CYCLE_LENGTH = Math.PI * 2;

const getNumOfWraps = (tension: number) =>
  Math.round(normalize(tension, 30, 400, 2, 18));

export function calculatePoints({
  tension,
  dragY,
}: {
  tension: number;
  dragY: number;
}) {
  const effectiveHeight = SPRING_HEIGHT - SPRING_STEM_HEIGHT + dragY;

  const amountToOmit = 1;

  const numOfWraps = getNumOfWraps(tension);

  return range(effectiveHeight - amountToOmit).map((i) => {
    const isFinalPoint = i === effectiveHeight - 1;
    const ratio = i / effectiveHeight;

    const totalLength = SIN_CYCLE_LENGTH * numOfWraps;

    // Right now, `progress` ranges from 0 to 100.
    // Normalize this value to fit between 0 and `totalLength`.
    // Just cross-multiplying to get the normalized value:
    //
    // progress         positionInRads
    // --------  =      --------------
    //   1             totalLength
    //
    // prettier-ignore
    const positionInRads = (ratio * totalLength);

    // `sinValue` is a number from -1 to 1 representing where on the sin
    // wave this point is.
    // We need to transform that to a value that fits our given canvas.
    const sinValue = Math.sin(positionInRads);

    const x = isFinalPoint
      ? SPRING_WIDTH / 2
      : normalize(sinValue, -1, 1, 0, SPRING_WIDTH);

    return x;
  });
}

// Spring physics implementation
const { sqrt, exp, sin, cos } = Math;

function getSpring({
  k,
  c,
  m,
  x0,
  v0,
}: {
  k: number;
  c: number;
  m: number;
  x0: number;
  v0: number;
}) {
  const radicand = c * c - 4 * k * m;
  if (radicand > 0) {
    const rp = (-c + sqrt(radicand)) / (2 * m);
    const rn = (-c - sqrt(radicand)) / (2 * m);
    const a = (x0 * rp - v0) / (rp - rn);
    const b = (v0 - x0 * rn) / (rp - rn);
    return (t: number): [number, number] => [
      a * exp(rn * t) + b * exp(rp * t),
      a * rn * exp(rn * t) + b * rp * exp(rp * t),
    ];
  } else if (radicand < 0) {
    const r = -c / (2 * m);
    const s = sqrt(-radicand) / (2 * m);
    const a = x0;
    const b = (v0 - r * x0) / s;
    return (t: number): [number, number] => [
      exp(r * t) * (a * cos(s * t) + b * sin(s * t)),
      exp(r * t) *
        ((b * s + a * r) * cos(s * t) - (a * s - b * r) * sin(s * t)),
    ];
  } else {
    const r = -c / (2 * m);
    const a = x0;
    const b = v0 - r * x0;
    return (t: number): [number, number] => [
      (a + b * t) * exp(r * t),
      (b + a * r + b * r * t) * exp(r * t),
    ];
  }
}

function newSpring(
  target: number,
  x0: number,
  v0: number,
  start: number,
  k: number,
  c: number,
  m: number
) {
  const d = target - x0;
  const spring = getSpring({ k, c, m, x0: d, v0 });
  return (now: number): [number, number] => {
    const t = (now - start) / 1000;
    const [x, v] = spring(t);
    return [target - x, v];
  };
}

/**
 * Calculates the spring position based on physics parameters
 * @param displacement - The initial displacement from equilibrium
 * @param tension - Spring tension (higher = stiffer spring)
 * @param friction - Damping factor (higher = more damping)
 * @param elapsed - Time elapsed since spring release (in milliseconds)
 */
export function calculateSpringPosition(
  displacement: number,
  tension: number,
  friction: number,
  mass: number,
  elapsed: number
): number {
  // Spring parameters
  const target = 0; // Equilibrium position
  const x0 = displacement; // Initial displacement
  const v0 = 0; // Initial velocity
  const start = 0; // Start time

  // Create spring function
  const spring = newSpring(
    target,
    x0,
    v0,
    start,
    tension,
    friction,
    mass
  );

  // Calculate position at the given time
  const [position] = spring(elapsed);

  return position;
}
